// ═══════════════════════════════════════════════════════════════
// HotWash — Submit Observation Form
// Full-width centered layout
// ═══════════════════════════════════════════════════════════════

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, TextInput, Textarea, Select, Checkbox, Button,
  Group, Stack, SimpleGrid, Badge,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import { IconSend, IconUser, IconX } from '@tabler/icons-react';
import { useHotWashStore } from '../../store/hotwashStore';
import { useAuthStore } from '../../store/authStore';
import { TIER_META, TIER_KEYS, CATEGORIES, PHASES } from '../../types';
import type { ObservationTier, ObservationCategory, ObservationPhase } from '../../types';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

export default function SubmitObservation() {
  const navigate = useNavigate();
  const { submitObservation } = useHotWashStore();
  const { activeIncident, incidents } = useAuthStore();

  const [incidentId, setIncidentId] = useState<string>(activeIncident ? String(activeIncident.id) : '');
  const [tier, setTier] = useState<ObservationTier>('observation');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ObservationCategory>('communication');
  const [phase, setPhase] = useState<ObservationPhase>('response');
  const [suggestedRemedy, setSuggestedRemedy] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Person picker for assignedPersonId
  const [assignedPersonId, setAssignedPersonId] = useState<string | null>(null);
  const [assignedPersonName, setAssignedPersonName] = useState('');
  const [personQuery, setPersonQuery] = useState('');
  const [personResults, setPersonResults] = useState<Array<{ id: string; name: string; title: string }>>([]);
  const [personSearching, setPersonSearching] = useState(false);
  const personDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePersonQuery = (q: string) => {
    setPersonQuery(q);
    if (personDebounce.current) clearTimeout(personDebounce.current);
    if (!q.trim() || !TABLES.people) { setPersonResults([]); return; }
    personDebounce.current = setTimeout(async () => {
      setPersonSearching(true);
      try {
        const FP = FIELDS.people;
        const res = await queryRecords<Record<string, unknown>>(TABLES.people, {
          filters: [{ field: FP.status, operator: 'eq', value: 'active' }],
          search: q,
          limit: 10,
        });
        setPersonResults(res.records.map(r => ({
          id: String(r.record_id),
          name: `${String(r[FP.firstName] ?? '')} ${String(r[FP.lastName] ?? '')}`.trim(),
          title: String(r[FP.title] ?? ''),
        })));
      } catch { /* ignore */ } finally { setPersonSearching(false); }
    }, 300);
  };

  const incidentOptions = incidents
    .filter(i => i.id != null)
    .map(i => ({ value: String(i.id), label: `${i.name} (${i.status})` }));

  const catOptions = CATEGORIES.map(c => ({
    value: c,
    label: c.charAt(0).toUpperCase() + c.slice(1),
  }));

  const phaseOptions = PHASES.map(p => ({
    value: p,
    label: p.charAt(0).toUpperCase() + p.slice(1),
  }));

  const handleSubmit = async () => {
    if (!incidentId || !title.trim()) {
      notifications.show({ title: 'Required', message: 'Select an incident and enter a title.', color: 'var(--color-error)' });
      return;
    }
    setSubmitting(true);
    try {
      await submitObservation({
        incidentId,
        tier,
        title: title.trim(),
        description: description.trim(),
        category,
        phase,
        suggestedRemedy: suggestedRemedy.trim(),
        isAnonymous,
        ...(assignedPersonId ? { assignedPersonId } : {}),
      });
      notifications.show({ title: 'Submitted', message: 'Observation recorded.', color: 'var(--color-success)' });
      navigate('/hotwash/observations');
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to submit.',
        color: 'var(--color-error)',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Title order={3} mb="md" c="black">Submit Observation</Title>

      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        {/* Left: Form */}
        <Paper p="lg" radius="sm" withBorder>
          <Stack gap="sm">
            <Select
              label="Incident"
              placeholder="Select incident"
              data={incidentOptions}
              value={incidentId}
              onChange={v => setIncidentId(v ?? '')}
              required
            />

            <TextInput
              label="Title"
              placeholder="Brief summary of the observation"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />

            <Textarea
              label="Description"
              placeholder="Detailed description of what was observed..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              minRows={4}
              autosize
            />

            <Group grow>
              <Select
                label="Category"
                data={catOptions}
                value={category}
                onChange={v => setCategory((v as ObservationCategory) ?? 'other')}
              />
              <Select
                label="Phase"
                data={phaseOptions}
                value={phase}
                onChange={v => setPhase((v as ObservationPhase) ?? 'response')}
              />
            </Group>

            <Textarea
              label="Suggested Remedy"
              placeholder="What should be done differently next time?"
              value={suggestedRemedy}
              onChange={e => setSuggestedRemedy(e.target.value)}
              minRows={2}
              autosize
            />

            {/* Assigned Person picker (optional) */}
            {TABLES.people && (
              <div>
                <Text size="sm" fw={500} mb={4}>Assign To (optional)</Text>
                {assignedPersonId ? (
                  <Badge
                    size="sm"
                    leftSection={<IconUser size={10} />}
                    rightSection={
                      <IconX size={10} style={{ cursor: 'pointer' }} onClick={() => { setAssignedPersonId(null); setAssignedPersonName(''); setPersonQuery(''); }} />
                    }
                    style={{ cursor: 'default' }}
                  >
                    {assignedPersonName}
                  </Badge>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <TextInput
                      size="sm"
                      placeholder="Search person by name..."
                      leftSection={<IconUser size={14} />}
                      value={personQuery}
                      onChange={e => handlePersonQuery(e.target.value)}
                    />
                    {(personResults.length > 0 || personSearching) && (
                      <Paper withBorder style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, maxHeight: 180, overflowY: 'auto' }}>
                        {personSearching ? (
                          <Text size="xs" c="dimmed" p="xs">Searching...</Text>
                        ) : personResults.map(p => (
                          <div key={p.id} style={{ padding: '8px 8px', cursor: 'pointer' }}
                            onMouseDown={() => { setAssignedPersonId(p.id); setAssignedPersonName(p.name); setPersonQuery(''); setPersonResults([]); }}>
                            <Text size="sm" fw={500}>{p.name}</Text>
                            {p.title && <Text size="xs" c="dimmed">{p.title}</Text>}
                          </div>
                        ))}
                      </Paper>
                    )}
                  </div>
                )}
              </div>
            )}

            <Checkbox
              label="Submit anonymously"
              checked={isAnonymous}
              onChange={e => setIsAnonymous(e.currentTarget.checked)}
              size="sm"
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={() => navigate('/hotwash')}>Cancel</Button>
              <Button
                leftSection={<IconSend size={14} />}
                onClick={handleSubmit}
                loading={submitting}
              >
                Submit Observation
              </Button>
            </Group>
          </Stack>
        </Paper>

        {/* Right: Tier Selector */}
        <div>
          <Text fw={600} size="sm" mb="sm">Observation Tier</Text>
          <Stack gap="xs">
            {TIER_KEYS.map(t => {
              const meta = TIER_META[t];
              const selected = tier === t;
              return (
                <Paper
                  key={t}
                  p="sm"
                  radius="sm"
                  withBorder
                  style={{
                    cursor: 'pointer',
                    borderColor: selected ? meta.color : undefined,
                    borderWidth: selected ? 2 : 1,
                    background: selected ? meta.bg : undefined,
                  }}
                  onClick={() => setTier(t)}
                >
                  <Group gap="sm">
                    <div style={{
                      width: 12, height: 12, borderRadius: '50%',
                      background: meta.color,
                      border: selected ? '2px solid #fff' : 'none',
                      boxShadow: selected ? `0 0 0 2px ${meta.color}` : 'none',
                    }} />
                    <div>
                      <Text size="sm" fw={600} style={{ color: meta.color }}>{meta.label}</Text>
                      <Text size="xs" c="dimmed">{meta.desc}</Text>
                    </div>
                  </Group>
                </Paper>
              );
            })}
          </Stack>

          <Paper p="sm" radius="sm" withBorder mt="md" bg="var(--bg-secondary)">
            <Text size="xs" fw={600} mb={4}>Tier Guide</Text>
            <Text size="xs" c="dimmed">
              <strong>Observation</strong> — Neutral finding, no action needed.<br />
              <strong>Improvement Needed</strong> — Area for growth, non-critical.<br />
              <strong>Problem Identified</strong> — Significant issue requiring follow-up.<br />
              <strong>Critical Error</strong> — Immediate action required, safety risk.
            </Text>
          </Paper>
        </div>
      </SimpleGrid>
    </div>
  );
}
